// The seam between the base and hidden ability sets. Every consumer (world, main, hud, menu)
// imports the active roster + ability system from here instead of directly from balance.ts /
// abilities.ts, so a single flag (see variant.ts) swaps the whole set per page: /play and /
// get the base game, /hidden gets the kids' custom roster. Resolved once at module load.

import { VARIANT } from './variant';
import { ABILITIES as BASE_ABILITIES } from './balance';
import type { AbilityDef } from './balance';
import { ABILITIES_HIDDEN } from './balanceHidden';
import { AbilitySystem } from './abilities';
import { AbilitySystemHidden } from './abilitiesHidden';
import type { LevelDefinition } from './levels';

/** The ability roster for this page (definitions: names, hotkeys, unlock levels, colors). */
export const ABILITIES = VARIANT === 'hidden' ? ABILITIES_HIDDEN : BASE_ABILITIES;

/** Either concrete ability system - both expose the same public surface the World uses. */
export type ActiveAbilitySystem = AbilitySystem | AbilitySystemHidden;

export function usesHiddenBaseAbilities(level: LevelDefinition): boolean {
  return VARIANT === 'hidden' && level.id === 'base_lab';
}

/** Private expansion URLs use their arena-native power roster, while the original
 * /hidden Base Lab keeps the children's custom roster unchanged. */
export function abilityDefinitionsForLevel(level: LevelDefinition): ReadonlyArray<AbilityDef> {
  return usesHiddenBaseAbilities(level) ? ABILITIES_HIDDEN : level.abilities;
}

/** Instantiate the ability system matching this page's variant. */
export function createAbilitySystem(
  defs: ReadonlyArray<AbilityDef> = BASE_ABILITIES,
  useHiddenSystem = VARIANT === 'hidden',
): ActiveAbilitySystem {
  return useHiddenSystem ? new AbilitySystemHidden() : new AbilitySystem(defs);
}
